import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Fab,
  Menu,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Rating,
  Avatar,
  Tooltip,
  Pagination,
  Popover,
} from '@mui/material'
import {
  Search,
  Add,
  Refresh,
  ViewList,
  Map as MapIcon,
  Edit,
  Delete,
  Visibility,
  EmojiEvents,
  TrendingUp,
  Person as PersonIcon,
  Group as GroupIcon,
  Explore,
  ArrowDropDown,
  Collections,
  ExitToApp,
  History,
  Check as CheckIcon,
  Close as CloseIcon,
  RateReview as ReviewIcon,
  FormatListBulleted as ListIcon,
  Star as StarIcon,
  // Role icons
  WorkspacePremium as OwnerIcon,
  Build as MaintainerIcon,
  Shield as ModeratorIcon,
  Science as TesterIcon,
  // Status icons
  EditNote as DraftIcon,
  BugReport as TestIcon,
  Public as PublishedIcon,
  Block as ClosedIcon,
} from '@mui/icons-material'
import { adventuresApi } from '@api/endpoints/adventures'
import { sessionsApi } from '@api/endpoints/sessions'
import { ratingsApi } from '@api/endpoints/ratings'
import type { RatingDetailedDto } from '@api/types/ratings'
import type { AdventureDto } from '@api/types/adventure'
import { SessionStatus } from '@api/types/session'
import { getAdventureStatusLabel, getAdventureStatusColor, AdventureStatus, ADVENTURE_MAIN_CATEGORY_LABELS, AdventureMainCategory } from '../types/adventure-editor.types'
import { AdventureHistoryDialog } from '../components/editor/AdventureHistoryDialog'
import { useAuthStore } from '@store/authStore'
import { canCreateSilentAdventures } from '@utils/userFlags'
import { VolumeOff, Public, Lock, PlayArrow, FilterList, LocationOn, CalendarToday, Apps, AccessTime } from '@mui/icons-material'
import { AdventureMap } from '@components/map/AdventureMap'
import { useMyCollections, useDeleteCollection } from '../hooks/useAdventureCollections'
import type { AdventureCollectionSummaryDto } from '@api/types/adventureCollection'

type ViewMode = 'grid' | 'list' | 'map'
type TabValue = 0 | 1 | 2 | 3 | 4
type SortField = 'title' | 'category' | 'allowGroups' | 'isRepeatable' | 'createdDate'
type SortOrder = 'asc' | 'desc'

// Tab name to index mapping
const TAB_ROUTES: Record<string, TabValue> = {
  active: 0,
  history: 1,
  manage: 2,
  collections: 3,
  silent: 4,
}

const TAB_NAMES: Record<TabValue, string> = {
  0: 'active',
  1: 'history',
  2: 'manage',
  3: 'collections',
  4: 'silent',
}

// User-Adventure relationship flags
enum UserAdventureRelationType {
  None = 0,
  Joined = 1,      // User has joined as participant
  Maintainer = 2,  // User is creator or maintainer
  Tester = 4,      // User is a tester
  Moderator = 8    // User is a moderator
}

export default function MyAdventuresPage() {
  const navigate = useNavigate()
  const { tab } = useParams<{ tab?: string }>()
  const [searchParams] = useSearchParams()

  // Derive active tab from URL param
  const activeTab: TabValue = tab && TAB_ROUTES[tab] !== undefined ? TAB_ROUTES[tab] : 0

  // Derive view mode from URL query param
  const viewParam = searchParams.get('view')
  const viewMode: ViewMode = (viewParam === 'grid' || viewParam === 'list' || viewParam === 'map') ? viewParam : 'grid'
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [adventureToDelete, setAdventureToDelete] = useState<AdventureDto | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false)
  const [adventureToAbandon, setAdventureToAbandon] = useState<AdventureDto | null>(null)
  const [abandonError, setAbandonError] = useState<string | null>(null)
  const [isAbandoning, setIsAbandoning] = useState(false)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [adventureForHistory, setAdventureForHistory] = useState<AdventureDto | null>(null)

  // Sorting state for list view
  const [sortField, setSortField] = useState<SortField>('title')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  // History tab sorting and pagination
  type HistorySortField = 'adventureTitle' | 'completedAt' | 'bestDuration' | 'completionCount'
  const [historySortField, setHistorySortField] = useState<HistorySortField>('completedAt')
  const [historySortOrder, setHistorySortOrder] = useState<SortOrder>('desc')
  const [historyPage, setHistoryPage] = useState(1)
  const historyPageSize = 10

  // Filter chip state for History and Manage tabs
  const [historyChip, setHistoryChip] = useState<string>('all')
  const [manageChip, setManageChip] = useState<string>('all')

  // Ratings dialog state
  const [ratingsDialogOpen, setRatingsDialogOpen] = useState(false)
  const [adventureForRatings, setAdventureForRatings] = useState<AdventureDto | null>(null)
  const [allRatings, setAllRatings] = useState<RatingDetailedDto[]>([])
  const [ratingsLoading, setRatingsLoading] = useState(false)

  // Stats popover state
  const [statsAnchorEl, setStatsAnchorEl] = useState<HTMLElement | null>(null)
  const [statsAdventure, setStatsAdventure] = useState<AdventureDto | null>(null)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // Responsive table column visibility helpers
  const hideOnMobile = { display: { xs: 'none', sm: 'table-cell' } } as const
  const hideOnSmall = { display: { xs: 'none', md: 'table-cell' } } as const

  // Check if user can create silent adventures
  const user = useAuthStore((state) => state.user)
  const userCanCreateSilentAdventures = canCreateSilentAdventures(user?.userFlags)

  // Fetch all adventures (for now - API doesn't support createdByMe/joined filters yet)
  const {
    data: allAdventures,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['myAdventures'],
    queryFn: async () => {
      console.log('[MyAdventuresPage] Fetching adventures...')
      const result = await adventuresApi.getAdventures({
        page: 1,
        pageSize: 50,
      })
      console.log('[MyAdventuresPage] Adventures result:', result)
      return result
    },
  })

  // Fetch silent adventures (only if user has permission)
  const {
    data: silentAdventures,
    isLoading: isLoadingSilent,
    refetch: refetchSilent,
  } = useQuery({
    queryKey: ['mySilentAdventures'],
    queryFn: async () => {
      console.log('[MyAdventuresPage] Fetching silent adventures...')
      const result = await adventuresApi.getMySilentAdventures()
      console.log('[MyAdventuresPage] Silent adventures result:', result)
      return result
    },
    enabled: userCanCreateSilentAdventures,
  })

  // Fetch completed sessions for history tab
  const {
    data: completedSessions,
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['myCompletedSessions'],
    queryFn: async () => {
      console.log('[MyAdventuresPage] Fetching completed sessions...')
      const result = await sessionsApi.getAllSessions({ includeCompleted: true })
      // Filter for completed sessions only
      const completed = (result || []).filter(s => s.status === SessionStatus.Completed)
      console.log('[MyAdventuresPage] Completed sessions:', completed)
      return completed
    },
  })

  // Fetch all sessions (for getting startedAt on Active tab)
  const { data: allSessions } = useQuery({
    queryKey: ['myAllSessions'],
    queryFn: async () => {
      const result = await sessionsApi.getAllSessions({ includeCompleted: true })
      return result || []
    },
  })

  // Map adventureId -> most recent active session's startedAt
  const adventureStartedMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!allSessions) return map
    // Filter for in-progress sessions (status 1) and sort by startedAt desc
    const activeSessions = allSessions
      .filter(s => s.status === SessionStatus.InProgress && s.startedAt)
      .sort((a, b) => {
        const dateA = new Date(a.startedAt!).getTime()
        const dateB = new Date(b.startedAt!).getTime()
        return dateB - dateA // Most recent first
      })
    // Map adventureId to the most recent startedAt
    for (const session of activeSessions) {
      if (!map.has(session.adventureId)) {
        map.set(session.adventureId, session.startedAt!)
      }
    }
    return map
  }, [allSessions])

  // Fetch user's collections
  const {
    data: myCollections,
    isLoading: isLoadingCollections,
    refetch: refetchCollections,
  } = useMyCollections()

  // Collection delete mutation
  const deleteCollectionMutation = useDeleteCollection()

  // Collection delete dialog state
  const [deleteCollectionDialogOpen, setDeleteCollectionDialogOpen] = useState(false)
  const [collectionToDelete, setCollectionToDelete] = useState<AdventureCollectionSummaryDto | null>(null)
  const [deleteCollectionError, setDeleteCollectionError] = useState<string | null>(null)

  // Debug logging
  useEffect(() => {
    console.log('[MyAdventuresPage] State:', {
      allAdventures,
      isLoading,
      error,
      token: localStorage.getItem('adventurecore_token')?.substring(0, 20) + '...',
    })
  }, [allAdventures, isLoading, error])

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    const tabName = TAB_NAMES[newValue]
    // Preserve view mode in query string if not default
    const viewQuery = viewMode !== 'grid' ? `?view=${viewMode}` : ''
    // Navigate to /my-adventures for active (default), or /my-adventures/{tab} for others
    if (newValue === 0) {
      navigate(`/my-adventures${viewQuery}`)
    } else {
      navigate(`/my-adventures/${tabName}${viewQuery}`)
    }
  }

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (!newMode) return
    // Update URL with new view mode
    const tabPath = activeTab === 0 ? '/my-adventures' : `/my-adventures/${TAB_NAMES[activeTab]}`
    const viewQuery = newMode !== 'grid' ? `?view=${newMode}` : ''
    navigate(`${tabPath}${viewQuery}`, { replace: true })
  }

  const handleRefresh = () => {
    refetch()
    refetchHistory()
    refetchCollections()
    if (userCanCreateSilentAdventures) {
      refetchSilent()
    }
  }

  // Helper to check if user has a specific relation type (flags-based)
  const hasRelation = (relationType: number | undefined, flag: UserAdventureRelationType): boolean => {
    if (relationType === undefined || relationType === null) return false
    return (relationType & flag) !== 0
  }

  const getAdventures = () => {
    // Handle both array response and paginated response
    const adventures = Array.isArray(allAdventures)
      ? allAdventures
      : allAdventures?.items

    if (!adventures) return []

    // Filter by tab using userRelationType
    let filteredByTab: AdventureDto[] = []

    if (activeTab === 0) {
      // "Active" tab - show Joined (1) or Tester (4)
      filteredByTab = adventures.filter((adv) => {
        const relation = (adv as any).userRelationType as number | undefined
        return hasRelation(relation, UserAdventureRelationType.Joined) ||
               hasRelation(relation, UserAdventureRelationType.Tester)
      })
    } else if (activeTab === 1) {
      // History tab - show completed sessions as adventure cards
      // We'll handle this separately in the render since it uses session data
      filteredByTab = []
    } else if (activeTab === 2) {
      // "Manage" tab - show Maintainer (2)
      filteredByTab = adventures.filter((adv) => {
        const relation = (adv as any).userRelationType as number | undefined
        return hasRelation(relation, UserAdventureRelationType.Maintainer)
      })
    } else if (activeTab === 3) {
      // Collections tab - empty for now
      filteredByTab = []
    } else if (activeTab === 4) {
      // Silent Adventures tab - use separate data source
      return (silentAdventures || []).filter((adv) => {
        const matchesSearch =
          searchQuery === '' ||
          adv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          adv.description?.toLowerCase().includes(searchQuery.toLowerCase())

        const matchesStatus = statusFilter === 'all' || adv.status === statusFilter || String(adv.status) === String(statusFilter)

        return matchesSearch && matchesStatus
      })
    } else {
      filteredByTab = []
    }

    // Apply search and status filters
    return filteredByTab.filter((adv) => {
      const matchesSearch =
        searchQuery === '' ||
        adv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        adv.description?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus = statusFilter === 'all' || adv.status === statusFilter || String(adv.status) === String(statusFilter)

      return matchesSearch && matchesStatus
    })
  }

  // Get count of joined adventures for tab label
  const getJoinedCount = () => {
    const adventures = Array.isArray(allAdventures)
      ? allAdventures
      : allAdventures?.items
    if (!adventures) return 0
    return adventures.filter((adv) => {
      const relation = (adv as any).userRelationType as number | undefined
      return hasRelation(relation, UserAdventureRelationType.Joined) ||
             hasRelation(relation, UserAdventureRelationType.Tester)
    }).length
  }

  // Get count of manageable adventures for tab label
  const getManageableCount = () => {
    const adventures = Array.isArray(allAdventures)
      ? allAdventures
      : allAdventures?.items
    if (!adventures) return 0
    return adventures.filter((adv) => {
      const relation = (adv as any).userRelationType as number | undefined
      return hasRelation(relation, UserAdventureRelationType.Maintainer)
    }).length
  }

  // Get count of silent adventures for tab label
  const getSilentCount = () => {
    return silentAdventures?.length || 0
  }

  // Get count of collections for tab label
  const getCollectionsCount = () => {
    return myCollections?.length || 0
  }

  // Get count of unique completed adventures for history tab
  const getHistoryCount = () => {
    return groupedHistorySessions.length
  }

  // Collection delete handlers
  const handleDeleteCollectionClick = (collection: AdventureCollectionSummaryDto) => {
    setCollectionToDelete(collection)
    setDeleteCollectionError(null)
    setDeleteCollectionDialogOpen(true)
  }

  const handleDeleteCollectionConfirm = async () => {
    if (!collectionToDelete) return

    try {
      await deleteCollectionMutation.mutateAsync(collectionToDelete.id)
      setDeleteCollectionDialogOpen(false)
      setCollectionToDelete(null)
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to delete collection'
      setDeleteCollectionError(message)
    }
  }

  const handleDeleteCollectionCancel = () => {
    setDeleteCollectionDialogOpen(false)
    setCollectionToDelete(null)
    setDeleteCollectionError(null)
  }

  // Format UTC date string to local date (appends Z to ensure UTC parsing)
  const formatUtcDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-'
    // Append Z if not present to ensure UTC parsing
    const utcStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
    return new Date(utcStr).toLocaleDateString()
  }

  // Calculate duration in seconds from startedAt and completedAt
  const calculateDuration = (startedAt: string | null, completedAt: string | null): number => {
    if (!startedAt || !completedAt) return 0
    // Append Z to ensure UTC parsing
    const startUtc = startedAt.endsWith('Z') ? startedAt : startedAt + 'Z'
    const endUtc = completedAt.endsWith('Z') ? completedAt : completedAt + 'Z'
    const start = new Date(startUtc).getTime()
    const end = new Date(endUtc).getTime()
    return Math.max(0, Math.floor((end - start) / 1000))
  }

  // Format duration for display (from seconds)
  const formatDuration = (seconds: number): string => {
    if (seconds === 0) return '-'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }

  // Parse and format duration from API timespan string (e.g., "00:06:20.7743898" or "2.14:15:38.7483527")
  const formatDurationString = (durationStr: string | null | undefined): string => {
    if (!durationStr) return '-'

    // Parse format: [d.]hh:mm:ss[.fffffff]
    const dayMatch = durationStr.match(/^(\d+)\.(\d{2}):(\d{2}):(\d{2})/)
    const timeMatch = durationStr.match(/^(\d{2}):(\d{2}):(\d{2})/)

    let days = 0, hours = 0, minutes = 0, seconds = 0

    if (dayMatch) {
      days = parseInt(dayMatch[1], 10)
      hours = parseInt(dayMatch[2], 10)
      minutes = parseInt(dayMatch[3], 10)
      seconds = parseInt(dayMatch[4], 10)
    } else if (timeMatch) {
      hours = parseInt(timeMatch[1], 10)
      minutes = parseInt(timeMatch[2], 10)
      seconds = parseInt(timeMatch[3], 10)
    } else {
      return '-'
    }

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }

  // Parse duration string to seconds for calculations
  const parseDurationToSeconds = (durationStr: string | null | undefined): number | null => {
    if (!durationStr) return null

    // Parse format: [d.]hh:mm:ss[.fffffff]
    const dayMatch = durationStr.match(/^(\d+)\.(\d{2}):(\d{2}):(\d{2})/)
    const timeMatch = durationStr.match(/^(\d{2}):(\d{2}):(\d{2})/)

    let days = 0, hours = 0, minutes = 0, seconds = 0

    if (dayMatch) {
      days = parseInt(dayMatch[1], 10)
      hours = parseInt(dayMatch[2], 10)
      minutes = parseInt(dayMatch[3], 10)
      seconds = parseInt(dayMatch[4], 10)
    } else if (timeMatch) {
      hours = parseInt(timeMatch[1], 10)
      minutes = parseInt(timeMatch[2], 10)
      seconds = parseInt(timeMatch[3], 10)
    } else {
      return null
    }

    return days * 86400 + hours * 3600 + minutes * 60 + seconds
  }

  // Format seconds back to duration string for display
  const formatSecondsAsDuration = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return '-'
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }

  // Group completed sessions by adventureId with aggregated stats
  interface GroupedHistoryItem {
    adventureId: string
    adventureTitle: string
    completionCount: number
    lastCompletedAt: string
    bestDurationSeconds: number | null
    averageDurationSeconds: number | null
    isSolo: boolean // most recent
    isTestSession: boolean // any test sessions
  }

  const groupedHistorySessions = useMemo((): GroupedHistoryItem[] => {
    if (!completedSessions) return []

    // Only include sessions with a completedAt date
    const validSessions = completedSessions.filter(s => s.completedAt)

    // Group by adventureId
    const grouped = new Map<string, typeof validSessions>()
    for (const session of validSessions) {
      const existing = grouped.get(session.adventureId) || []
      existing.push(session)
      grouped.set(session.adventureId, existing)
    }

    // Convert to aggregated items
    const result: GroupedHistoryItem[] = []
    for (const [adventureId, sessions] of grouped) {
      // Sort by completedAt descending to get most recent first (append Z for UTC)
      sessions.sort((a, b) => {
        const dateA = new Date(a.completedAt!.endsWith('Z') ? a.completedAt! : a.completedAt! + 'Z').getTime()
        const dateB = new Date(b.completedAt!.endsWith('Z') ? b.completedAt! : b.completedAt! + 'Z').getTime()
        return dateB - dateA
      })

      const mostRecent = sessions[0]

      // Calculate durations from startedAt and completedAt
      const durations = sessions
        .map(s => {
          // First try the duration field if it exists
          const durationFromField = parseDurationToSeconds((s as any).duration)
          if (durationFromField !== null && durationFromField > 0) {
            return durationFromField
          }
          // Fall back to calculating from startedAt and completedAt (append Z for UTC)
          if (s.startedAt && s.completedAt) {
            const startUtc = s.startedAt.endsWith('Z') ? s.startedAt : s.startedAt + 'Z'
            const endUtc = s.completedAt.endsWith('Z') ? s.completedAt : s.completedAt + 'Z'
            const start = new Date(startUtc).getTime()
            const end = new Date(endUtc).getTime()
            const durationSeconds = Math.floor((end - start) / 1000)
            return durationSeconds > 0 ? durationSeconds : null
          }
          return null
        })
        .filter((d): d is number => d !== null && d > 0)

      const bestDuration = durations.length > 0 ? Math.min(...durations) : null
      const avgDuration = durations.length > 0 ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null

      result.push({
        adventureId,
        adventureTitle: mostRecent.adventureTitle,
        completionCount: sessions.length,
        lastCompletedAt: mostRecent.completedAt!,
        bestDurationSeconds: bestDuration,
        averageDurationSeconds: avgDuration,
        isSolo: mostRecent.isSolo,
        isTestSession: sessions.some(s => s.isTestSession),
      })
    }

    return result
  }, [completedSessions])

  // Filter grouped history based on chip selection and search
  const filteredGroupedHistory = useMemo((): GroupedHistoryItem[] => {
    let items = groupedHistorySessions
    if (historyChip === 'completed') items = items.filter(h => h.completionCount === 1)
    if (historyChip === '2x') items = items.filter(h => h.completionCount >= 2)
    if (historyChip === 'solo') items = items.filter(h => h.isSolo)
    if (historyChip === 'group') items = items.filter(h => !h.isSolo)
    return items
  }, [groupedHistorySessions, historyChip])

  // Handle history sort
  const handleHistorySort = (field: typeof historySortField) => {
    if (historySortField === field) {
      setHistorySortOrder(historySortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setHistorySortField(field)
      setHistorySortOrder(field === 'completedAt' ? 'desc' : 'asc')
    }
    setHistoryPage(1) // Reset to first page on sort change
  }

  // Sorted and paginated grouped history sessions
  const sortedGroupedHistory = useMemo(() => {
    return [...filteredGroupedHistory].sort((a, b) => {
      let comparison = 0
      switch (historySortField) {
        case 'adventureTitle':
          comparison = a.adventureTitle.localeCompare(b.adventureTitle)
          break
        case 'completedAt':
          const dateA = new Date(a.lastCompletedAt.endsWith('Z') ? a.lastCompletedAt : a.lastCompletedAt + 'Z').getTime()
          const dateB = new Date(b.lastCompletedAt.endsWith('Z') ? b.lastCompletedAt : b.lastCompletedAt + 'Z').getTime()
          comparison = dateA - dateB
          break
        case 'bestDuration':
          const durationA = a.bestDurationSeconds ?? Infinity
          const durationB = b.bestDurationSeconds ?? Infinity
          comparison = durationA - durationB
          break
        case 'completionCount':
          comparison = a.completionCount - b.completionCount
          break
      }
      return historySortOrder === 'asc' ? comparison : -comparison
    })
  }, [filteredGroupedHistory, historySortField, historySortOrder])

  const paginatedGroupedHistory = useMemo(() => {
    const startIndex = (historyPage - 1) * historyPageSize
    return sortedGroupedHistory.slice(startIndex, startIndex + historyPageSize)
  }, [sortedGroupedHistory, historyPage, historyPageSize])

  const historyTotalPages = Math.ceil(sortedGroupedHistory.length / historyPageSize)

  // Get adventures with location data for map view (per-tab)
  // Helper to check if adventure has valid location (either direct lat/lng or nested location object)
  const hasValidLocation = (adv: AdventureDto): boolean => {
    // Check direct latitude/longitude properties first
    if (adv.latitude !== undefined && adv.longitude !== undefined) {
      return true
    }
    // Fall back to nested location object
    if (adv.location?.latitude !== undefined && adv.location?.longitude !== undefined) {
      return true
    }
    return false
  }

  // Normalize adventure to have location object for AdventureMap compatibility
  const normalizeLocation = (adv: AdventureDto): AdventureDto => {
    if (adv.location?.latitude !== undefined && adv.location?.longitude !== undefined) {
      return adv
    }
    if (adv.latitude !== undefined && adv.longitude !== undefined) {
      return {
        ...adv,
        location: {
          latitude: adv.latitude,
          longitude: adv.longitude,
        }
      }
    }
    return adv
  }

  const getMapAdventures = (): AdventureDto[] => {
    const allAdvs = Array.isArray(allAdventures)
      ? allAdventures
      : allAdventures?.items || []

    if (activeTab === 0) {
      // Active tab
      return allAdvs
        .filter((adv) => {
          const relation = (adv as any).userRelationType as number | undefined
          const hasRelationship = hasRelation(relation, UserAdventureRelationType.Joined) ||
                 hasRelation(relation, UserAdventureRelationType.Tester)
          return hasRelationship && hasValidLocation(adv)
        })
        .map(normalizeLocation)
    } else if (activeTab === 1) {
      // History tab - map grouped sessions to adventures with location, including aggregated completion data
      const adventureMap = new Map(allAdvs.map(a => [a.id, a]))
      return groupedHistorySessions
        .map(grouped => {
          const adv = adventureMap.get(grouped.adventureId)
          if (!adv) return undefined
          // Attach grouped session completion data to adventure for map popup
          return {
            ...adv,
            sessionCompletedAt: grouped.lastCompletedAt,
            sessionDuration: grouped.bestDurationSeconds ? formatSecondsAsDuration(grouped.bestDurationSeconds) : null,
            sessionCompletionCount: grouped.completionCount,
            sessionAverageDuration: grouped.averageDurationSeconds ? formatSecondsAsDuration(grouped.averageDurationSeconds) : null,
          }
        })
        .filter((adv): adv is AdventureDto => adv !== undefined && hasValidLocation(adv))
        .map(normalizeLocation)
    } else if (activeTab === 2) {
      // Manage tab
      return allAdvs
        .filter((adv) => {
          const relation = (adv as any).userRelationType as number | undefined
          const hasRelationship = hasRelation(relation, UserAdventureRelationType.Maintainer)
          return hasRelationship && hasValidLocation(adv)
        })
        .map(normalizeLocation)
    } else if (activeTab === 4) {
      // Silent Adventures tab
      return (silentAdventures || [])
        .filter(hasValidLocation)
        .map(normalizeLocation)
    }
    return []
  }

  const mapAdventures = useMemo(() => getMapAdventures(), [allAdventures, groupedHistorySessions, silentAdventures, activeTab])

  const adventures = getAdventures()

  // Sorted adventures for list view
  const sortedAdventures = useMemo(() => {
    return [...adventures].sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title)
          break
        case 'category':
          comparison = ((a as any).mainCategory || 0) - ((b as any).mainCategory || 0)
          break
        case 'allowGroups':
          comparison = (a.allowGroups ? 1 : 0) - (b.allowGroups ? 1 : 0)
          break
        case 'isRepeatable':
          comparison = (a.isRepeatable ? 1 : 0) - (b.isRepeatable ? 1 : 0)
          break
        case 'createdDate':
          comparison = new Date(a.createdDate || 0).getTime() - new Date(b.createdDate || 0).getTime()
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [adventures, sortField, sortOrder])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const handleCreateMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCreateMenuAnchor(event.currentTarget)
  }

  const handleCreateMenuClose = () => {
    setCreateMenuAnchor(null)
  }

  const handleDeleteClick = (adventure: AdventureDto) => {
    setAdventureToDelete(adventure)
    setDeleteError(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!adventureToDelete) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await adventuresApi.deleteAdventure(adventureToDelete.id)
      setDeleteDialogOpen(false)
      setAdventureToDelete(null)
      refetch()
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to delete adventure'
      setDeleteError(message)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setAdventureToDelete(null)
    setDeleteError(null)
  }

  const handleAbandonClick = (adventure: AdventureDto) => {
    setAdventureToAbandon(adventure)
    setAbandonError(null)
    setAbandonDialogOpen(true)
  }

  const handleAbandonConfirm = async () => {
    if (!adventureToAbandon) return

    setIsAbandoning(true)
    setAbandonError(null)

    try {
      await adventuresApi.abandonAdventure(adventureToAbandon.id)
      setAbandonDialogOpen(false)
      setAdventureToAbandon(null)
      refetch()
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to abandon adventure'
      setAbandonError(message)
    } finally {
      setIsAbandoning(false)
    }
  }

  const handleAbandonCancel = () => {
    setAbandonDialogOpen(false)
    setAdventureToAbandon(null)
    setAbandonError(null)
  }

  // Check if adventure is joined (not maintainer)
  const isJoinedAdventure = (adventure: AdventureDto): boolean => {
    const relation = (adventure as any).userRelationType as number | undefined
    return hasRelation(relation, UserAdventureRelationType.Joined) ||
           hasRelation(relation, UserAdventureRelationType.Tester)
  }

  const handleHistoryClick = (adventure: AdventureDto) => {
    setAdventureForHistory(adventure)
    setHistoryDialogOpen(true)
  }

  const handleHistoryClose = () => {
    setHistoryDialogOpen(false)
    setAdventureForHistory(null)
  }

  const handleOpenRatings = async (adventure: AdventureDto) => {
    setAdventureForRatings(adventure)
    setRatingsDialogOpen(true)
    setRatingsLoading(true)
    try {
      const response = await ratingsApi.getAdventureRatings(adventure.id, { page: 1, pageSize: 50 })
      if (Array.isArray(response)) {
        setAllRatings(response)
      } else if (response && response.items) {
        setAllRatings(response.items || [])
      } else {
        setAllRatings([])
      }
    } catch (error) {
      console.error('Failed to fetch ratings:', error)
      setAllRatings([])
    } finally {
      setRatingsLoading(false)
    }
  }

  const handleCloseRatings = () => {
    setRatingsDialogOpen(false)
    setAdventureForRatings(null)
    setAllRatings([])
  }

  // Stats popover handlers
  const handleStatsClick = (event: React.MouseEvent<HTMLElement>, adventure: AdventureDto) => {
    setStatsAnchorEl(event.currentTarget)
    setStatsAdventure(adventure)
  }

  const handleStatsClose = () => {
    setStatsAnchorEl(null)
    setStatsAdventure(null)
  }

  // Check if adventure has stats data
  const hasStats = (adventure: AdventureDto) =>
    adventure.uniquePlayersLastHour !== undefined ||
    adventure.uniquePlayersLast24Hours !== undefined ||
    adventure.uniquePlayersLastMonth !== undefined ||
    adventure.uniquePlayersLastYear !== undefined

  return (
    <Box>
      {/* Tab Bar */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={userCanCreateSilentAdventures ? 'scrollable' : 'fullWidth'}
          scrollButtons={userCanCreateSilentAdventures ? 'auto' : false}
          allowScrollButtonsMobile={userCanCreateSilentAdventures}
          sx={{
            '& .MuiTabs-indicator': { height: 2.5 },
            '& .MuiTab-root': {
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: 0.5,
              minWidth: 0,
              px: { xs: 1.5, md: 2.5 },
              py: 1.5,
            },
          }}
        >
          {[
            { label: 'Active', count: getJoinedCount(), idx: 0 },
            { label: 'History', count: getHistoryCount(), idx: 1 },
            { label: 'Manage', count: getManageableCount(), idx: 2 },
            { label: 'Collections', count: getCollectionsCount(), idx: 3 },
            ...(userCanCreateSilentAdventures ? [{ label: 'Silent', count: getSilentCount(), idx: 4 }] : []),
          ].map((tab) => (
            <Tab
              key={tab.idx}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <span>{tab.label}</span>
                  <Box
                    component="span"
                    sx={{
                      fontSize: '10px',
                      fontWeight: 700,
                      px: 0.75,
                      py: '1px',
                      borderRadius: '8px',
                      bgcolor: activeTab === tab.idx ? 'primary.main' : 'action.hover',
                      color: activeTab === tab.idx ? 'primary.contrastText' : 'text.secondary',
                    }}
                  >
                    {tab.count}
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Create Menu (hidden, triggered by FAB) */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={handleCreateMenuClose}
      >
        <MenuItem component={RouterLink} to="/adventures/create" onClick={handleCreateMenuClose}>
          <Add sx={{ mr: 1 }} /> Create Adventure
        </MenuItem>
        <MenuItem component={RouterLink} to="/collections/create" onClick={handleCreateMenuClose}>
          <Collections sx={{ mr: 1 }} /> Adventure Collection
        </MenuItem>
      </Menu>

      {/* Filter Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, bgcolor: 'background.default' }}>
        <TextField
          fullWidth
          placeholder={activeTab === 1 ? 'Search history...' : activeTab === 2 ? 'Search created adventures...' : 'Search my adventures...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'background.paper',
              fontSize: '13px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: '10px',
              bgcolor: 'background.paper',
              fontSize: '12px',
              fontWeight: 600,
              '& .MuiSelect-select': { py: '8.5px', pr: 3 },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value={AdventureStatus.Draft}>Draft</MenuItem>
            <MenuItem value={AdventureStatus.Testing}>Testing</MenuItem>
            <MenuItem value={AdventureStatus.Published}>Published</MenuItem>
            <MenuItem value={AdventureStatus.ModeratorReview}>In Review</MenuItem>
            <MenuItem value={AdventureStatus.Archived}>Archived</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: 1,
              borderColor: 'divider',
              borderRadius: '8px !important',
              px: 1,
              py: 0.75,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            },
          }}
        >
          <ToggleButton value="grid"><Apps sx={{ fontSize: 18 }} /></ToggleButton>
          <ToggleButton value="list"><ListIcon sx={{ fontSize: 18 }} /></ToggleButton>
          <ToggleButton value="map"><MapIcon sx={{ fontSize: 18 }} /></ToggleButton>
        </ToggleButtonGroup>
        {activeTab === 2 && (
          <IconButton
            onClick={handleCreateMenuOpen}
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '10px',
              width: 36,
              height: 36,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <Add sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* Sort/Filter Chips */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', gap: 0.75, px: 2, py: 1.25, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
          {['all', 'completed', '2x', 'solo', 'group'].map((chip) => (
            <Chip
              key={chip}
              label={chip === '2x' ? '2x+' : chip.charAt(0).toUpperCase() + chip.slice(1)}
              size="small"
              onClick={() => setHistoryChip(chip)}
              sx={{
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 600,
                border: 1,
                borderColor: historyChip === chip ? 'primary.main' : 'divider',
                bgcolor: historyChip === chip ? 'primary.main' : 'background.paper',
                color: historyChip === chip ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { bgcolor: historyChip === chip ? 'primary.dark' : 'action.hover' },
              }}
            />
          ))}
        </Box>
      )}
      {activeTab === 2 && (
        <Box sx={{ display: 'flex', gap: 0.75, px: 2, py: 1.25, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
          {['all', 'published', 'draft', 'review'].map((chip) => (
            <Chip
              key={chip}
              label={chip === 'review' ? 'In Review' : chip.charAt(0).toUpperCase() + chip.slice(1)}
              size="small"
              onClick={() => setManageChip(chip)}
              sx={{
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: 600,
                border: 1,
                borderColor: manageChip === chip ? 'primary.main' : 'divider',
                bgcolor: manageChip === chip ? 'primary.main' : 'background.paper',
                color: manageChip === chip ? 'primary.contrastText' : 'text.secondary',
                '&:hover': { bgcolor: manageChip === chip ? 'primary.dark' : 'action.hover' },
              }}
            />
          ))}
        </Box>
      )}

      {/* Content */}
      {(isLoading || (activeTab === 1 && isLoadingHistory) || (activeTab === 3 && isLoadingCollections) || (activeTab === 4 && isLoadingSilent)) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: isMobile ? 4 : 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading adventures. Please try again.
        </Alert>
      ) : activeTab === 3 ? (
        // Collections tab - show user's collections
        myCollections && myCollections.length > 0 ? (
          viewMode === 'grid' ? (
            // Grid view - mockup-styled collection cards
            <Box sx={{ px: 2, pt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
              {myCollections.map((collection: AdventureCollectionSummaryDto) => (
                <Card key={collection.id} sx={{ borderRadius: '16px', overflow: 'hidden', border: 1, borderColor: 'divider', boxShadow: 2 }}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: collection.isPublic ? 'success.light' : 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Collections sx={{ fontSize: 24, color: collection.isPublic ? 'success.dark' : 'text.secondary' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.25 }}>{collection.name}</Typography>
                      {collection.shortDescription && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.3 }}>{collection.shortDescription}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end', flexShrink: 0 }}>
                      <Chip
                        label={collection.isPublic ? 'Public' : 'Private'}
                        size="small"
                        sx={{
                          fontSize: '10px', fontWeight: 600, height: 20,
                          bgcolor: collection.isPublic ? '#dcfce7' : '#f3f4f6',
                          color: collection.isPublic ? '#166534' : '#6b7280',
                        }}
                      />
                      {collection.achievementCount > 0 && (
                        <Chip
                          label={`${collection.achievementCount} Achievement${collection.achievementCount !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{ fontSize: '10px', fontWeight: 600, height: 20, bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ px: 2, pb: 1.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={collection.totalAdventureCount > 0 ? Math.round(((collection as any).completedCount || 0) / collection.totalAdventureCount * 100) : 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: 3 } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {collection.totalAdventureCount} adventure{collection.totalAdventureCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {collection.achievementCount > 0 && (
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        <EmojiEvents sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'secondary.main' }} />
                        <strong>{collection.achievementCount}</strong> achievement{collection.achievementCount !== 1 ? 's' : ''}
                      </Typography>
                    )}
                    <Button
                      component={RouterLink}
                      to={`/collections/${collection.id}`}
                      size="small"
                      sx={{ fontSize: '12px', fontWeight: 600, color: 'primary.main', textTransform: 'none', ml: 'auto' }}
                    >
                      View all →
                    </Button>
                    {collection.canEdit && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCollectionClick(collection)}
                        title="Delete Collection"
                        sx={{ ml: 0.5 }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            // List or Map view - show as table (collections don't have location data)
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell sx={hideOnSmall}>Description</TableCell>
                    <TableCell align="center" sx={hideOnSmall}>Visibility</TableCell>
                    <TableCell align="center" sx={hideOnMobile}>Achievements</TableCell>
                    <TableCell align="center" sx={hideOnMobile}>Adventures</TableCell>
                    <TableCell sx={hideOnMobile}>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myCollections.map((collection: AdventureCollectionSummaryDto) => (
                    <TableRow key={collection.id} hover>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {collection.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...hideOnSmall, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {collection.shortDescription || '-'}
                      </TableCell>
                      <TableCell align="center" sx={hideOnSmall}>
                        <Chip
                          icon={collection.isPublic ? <Public /> : <Lock />}
                          label={collection.isPublic ? 'Public' : 'Private'}
                          size="small"
                          color={collection.isPublic ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center" sx={hideOnMobile}>
                        <Chip
                          icon={<EmojiEvents />}
                          label={collection.achievementCount}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center" sx={hideOnMobile}>
                        {collection.totalAdventureCount}
                      </TableCell>
                      <TableCell sx={hideOnMobile}>
                        {collection.createdDate ? new Date(collection.createdDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          component={RouterLink}
                          to={`/collections/${collection.id}`}
                          size="small"
                          title="View"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                        {collection.canEdit && (
                          <>
                            <IconButton
                              component={RouterLink}
                              to={`/collections/${collection.id}`}
                              size="small"
                              title="Edit"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCollectionClick(collection)}
                              title="Delete Collection"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 7.5, px: 3.75 }}>
            <Collections sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
              No collections found
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
              Collections help you organize related adventures together!
            </Typography>
            <Button
              component={RouterLink}
              to="/collections/create"
              variant="contained"
              startIcon={<Add />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              Create Collection
            </Button>
          </Box>
        )
      ) : activeTab === 1 ? (
        // History tab - show grouped completed sessions by adventure
        filteredGroupedHistory.length > 0 ? (
          viewMode === 'grid' ? (
            <Box sx={{ px: 2, pt: 0.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
              {filteredGroupedHistory.map((grouped) => (
                <Card
                  key={grouped.adventureId}
                  component={RouterLink}
                  to={`/adventures/${grouped.adventureId}`}
                  sx={{ borderRadius: '14px', p: '14px 16px', border: 1, borderColor: 'divider', boxShadow: 1, textDecoration: 'none', display: 'block' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Chip
                      label={grouped.completionCount > 1 ? `${grouped.completionCount}x Completed` : 'Completed'}
                      size="small"
                      icon={<CheckIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        fontWeight: 700, fontSize: '11px', height: 24,
                        bgcolor: grouped.completionCount > 1 ? '#e3f2fd' : '#e8f5e9',
                        color: grouped.completionCount > 1 ? '#1565c0' : '#2e7d32',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                      {formatUtcDate(grouped.lastCompletedAt)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
                    {grouped.adventureTitle}
                  </Typography>
                  {grouped.bestDurationSeconds !== null && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        <AccessTime sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                        Best: <strong>{formatSecondsAsDuration(grouped.bestDurationSeconds)}</strong>
                      </Typography>
                      {grouped.completionCount > 1 && grouped.averageDurationSeconds !== null && (
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          <TrendingUp sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                          Avg: <strong>{formatSecondsAsDuration(grouped.averageDurationSeconds)}</strong>
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <Chip
                        label={grouped.isSolo ? 'Solo' : 'Group'}
                        size="small"
                        sx={{
                          fontSize: '10px', fontWeight: 600, height: 22,
                          bgcolor: 'action.hover', border: 1, borderColor: 'divider', color: 'text.secondary',
                        }}
                      />
                      {grouped.isTestSession && (
                        <Chip
                          label="Test"
                          size="small"
                          sx={{ fontSize: '10px', fontWeight: 600, height: 22, bgcolor: 'warning.light', color: 'warning.dark' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : viewMode === 'list' ? (
            // List view for History tab (grouped by adventure)
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={historySortField === 'adventureTitle'}
                          direction={historySortField === 'adventureTitle' ? historySortOrder : 'asc'}
                          onClick={() => handleHistorySort('adventureTitle')}
                        >
                          Adventure
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={hideOnMobile}>
                        <TableSortLabel
                          active={historySortField === 'completedAt'}
                          direction={historySortField === 'completedAt' ? historySortOrder : 'asc'}
                          onClick={() => handleHistorySort('completedAt')}
                        >
                          Last Completed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={hideOnMobile}>
                        <TableSortLabel
                          active={historySortField === 'completionCount'}
                          direction={historySortField === 'completionCount' ? historySortOrder : 'asc'}
                          onClick={() => handleHistorySort('completionCount')}
                        >
                          Times
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={hideOnSmall}>
                        <TableSortLabel
                          active={historySortField === 'bestDuration'}
                          direction={historySortField === 'bestDuration' ? historySortOrder : 'asc'}
                          onClick={() => handleHistorySort('bestDuration')}
                        >
                          Best
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={hideOnSmall}>Avg</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedGroupedHistory.map((grouped) => (
                      <TableRow
                        key={grouped.adventureId}
                        hover
                        onClick={() => navigate(`/adventures/${grouped.adventureId}`)}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                      >
                        <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Chip
                              label={grouped.completionCount > 1 ? `${grouped.completionCount}x` : 'Completed'}
                              color="success"
                              size="small"
                              icon={<CheckIcon />}
                              sx={{ flexShrink: 0 }}
                            />
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {grouped.adventureTitle}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={hideOnMobile}>
                          <Typography variant="body2" color="text.secondary">
                            {formatUtcDate(grouped.lastCompletedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={hideOnMobile}>
                          <Typography variant="body2" color="text.secondary">
                            {grouped.completionCount}
                          </Typography>
                        </TableCell>
                        <TableCell sx={hideOnSmall}>
                          <Typography variant="body2" color="text.secondary">
                            {grouped.bestDurationSeconds !== null ? formatSecondsAsDuration(grouped.bestDurationSeconds) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={hideOnSmall}>
                          <Typography variant="body2" color="text.secondary">
                            {grouped.completionCount > 1 && grouped.averageDurationSeconds !== null
                              ? formatSecondsAsDuration(grouped.averageDurationSeconds)
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="View Adventure">
                            <IconButton
                              component={RouterLink}
                              to={`/adventures/${grouped.adventureId}`}
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
              </Table>
              </TableContainer>
              {historyTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={historyTotalPages}
                    page={historyPage}
                    onChange={(_, page) => setHistoryPage(page)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>
              )}
            </>
          ) : (
            // Map view for History tab
            mapAdventures.length > 0 ? (
              <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
                <AdventureMap
                  adventures={mapAdventures}
                  height="600px"
                />
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <MapIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Adventures with Location Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your completed adventures don't have location data to display on the map.
                </Typography>
              </Paper>
            )
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 7.5, px: 3.75 }}>
            <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
              No completed adventures yet
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
              Complete adventures to see them in your history!
            </Typography>
            <Button
              component={RouterLink}
              to="/adventures"
              variant="contained"
              startIcon={<Explore />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            >
              Browse Adventures
            </Button>
          </Box>
        )
      ) : adventures.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7.5, px: 3.75 }}>
          <Explore sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.75 }}>
            {activeTab === 4 ? 'No silent adventures found' : 'No adventures found'}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>
            {activeTab === 0
              ? 'Explore available adventures to get started!'
              : activeTab === 2
              ? 'Start your journey by creating your first adventure!'
              : 'Create your first silent adventure!'}
          </Typography>
          <Button
            component={RouterLink}
            to={activeTab === 0 ? '/adventures' : '/adventures/create'}
            variant="contained"
            startIcon={<Add />}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            {activeTab === 0 ? 'Browse Adventures' : activeTab === 2 ? 'Create Adventure' : 'Create Silent Adventure'}
          </Button>
        </Box>
      ) : viewMode === 'grid' ? (
        <Box sx={{ px: 2, pt: 0.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {adventures
            .filter(adv => {
              if (activeTab === 2 && manageChip !== 'all') {
                if (manageChip === 'published' && adv.status !== AdventureStatus.Published) return false
                if (manageChip === 'draft' && adv.status !== AdventureStatus.Draft) return false
                if (manageChip === 'review' && adv.status !== AdventureStatus.ModeratorReview) return false
              }
              return true
            })
            .map((adventure) => (
            activeTab === 0 ? (
              /* ===== Active Tab Card ===== */
              <Card key={adventure.id} sx={{ borderRadius: '16px', overflow: 'hidden', border: 1, borderColor: 'divider', boxShadow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ height: 140, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                  {(adventure.thumbnailImageUrl || adventure.coverImageUrl || adventure.imageUrl) ? (
                    <CardMedia
                      component="img"
                      height={140}
                      image={adventure.thumbnailImageUrl || adventure.coverImageUrl || adventure.imageUrl}
                      alt={adventure.title}
                      sx={{ objectFit: 'cover', width: '100%' }}
                    />
                  ) : (
                    <Box sx={{ height: 140, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})` }}>
                      <Explore sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
                    </Box>
                  )}
                  <LinearProgress
                    variant="determinate"
                    value={30}
                    sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                      bgcolor: 'rgba(0,0,0,0.15)',
                      '& .MuiLinearProgress-bar': { bgcolor: 'success.main', borderRadius: '0 2px 2px 0' },
                    }}
                  />
                  <Chip
                    label={adventure.status === AdventureStatus.Published ? 'Active' : adventure.status === AdventureStatus.Testing ? 'Testing' : 'New'}
                    size="small"
                    sx={{
                      position: 'absolute', top: 10, left: 10,
                      fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: 0.5,
                      bgcolor: adventure.status === AdventureStatus.Published ? 'rgba(64,145,108,0.9)' : adventure.status === AdventureStatus.Testing ? 'rgba(217,119,6,0.9)' : 'rgba(41,128,185,0.9)',
                      color: '#fff',
                    }}
                  />
                </Box>
                <Box sx={{ p: '14px 16px', cursor: 'pointer', flex: 1, display: 'flex', flexDirection: 'column' }} onClick={() => navigate(`/adventures/${adventure.id}`)}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                    {ADVENTURE_MAIN_CATEGORY_LABELS[(adventure as any).mainCategory as AdventureMainCategory] || 'Adventure'}
                  </Typography>
                  <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'text.primary', mb: 0.5, letterSpacing: -0.2 }}>
                    {adventure.title}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4, mb: 1.25, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {adventure.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 'auto' }}>
                    {(adventure as any).waypointCount !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '11px', color: 'text.secondary' }}>
                        <LocationOn sx={{ fontSize: 12 }} />
                        <strong>{(adventure as any).waypointCount}</strong> waypoints
                      </Box>
                    )}
                    {adventureStartedMap.get(adventure.id) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '11px', color: 'text.secondary' }}>
                        <CalendarToday sx={{ fontSize: 12 }} />
                        Started {formatUtcDate(adventureStartedMap.get(adventure.id))}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    component={RouterLink}
                    to={`/adventures/${adventure.id}`}
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrow />}
                    sx={{ flex: 1, borderRadius: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'none', py: 0.75 }}
                  >
                    Continue
                  </Button>
                  <Button
                    component={RouterLink}
                    to={`/adventures/${adventure.id}?details=true`}
                    size="small"
                    variant="outlined"
                    sx={{ flex: 1, borderRadius: '8px', fontSize: '12px', fontWeight: 600, textTransform: 'none', py: 0.75, borderColor: 'divider', color: 'text.primary' }}
                  >
                    Details
                  </Button>
                </Box>
              </Card>
            ) : (
              /* ===== Manage / Silent Tab Card ===== */
              <Card key={adventure.id} sx={{ borderRadius: '14px', overflow: 'hidden', border: 1, borderColor: 'divider', boxShadow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', gap: 1.5, p: '14px 16px', flex: 1 }}>
                  <Box
                    sx={{
                      width: 72, height: 72, borderRadius: '12px', flexShrink: 0,
                      overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: 'action.hover',
                    }}
                  >
                    {(adventure.thumbnailImageUrl || adventure.coverImageUrl || adventure.imageUrl) ? (
                      <CardMedia
                        component="img"
                        image={adventure.thumbnailImageUrl || adventure.coverImageUrl || adventure.imageUrl}
                        alt={adventure.title}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Explore sx={{ fontSize: 32, color: 'text.secondary' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ mb: 0.5 }}>
                      <Chip
                        label={getAdventureStatusLabel(adventure.status)}
                        size="small"
                        sx={{
                          fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: 0.5, height: 20,
                          bgcolor: adventure.status === AdventureStatus.Draft ? 'warning.light' :
                                   adventure.status === AdventureStatus.Published ? 'success.light' :
                                   adventure.status === AdventureStatus.ModeratorReview ? '#ede9fe' : 'action.hover',
                          color: adventure.status === AdventureStatus.Draft ? 'warning.dark' :
                                 adventure.status === AdventureStatus.Published ? 'success.dark' :
                                 adventure.status === AdventureStatus.ModeratorReview ? '#5b21b6' : 'text.secondary',
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary', mb: 0.375 }}>
                      {adventure.title}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {adventure.description}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, px: 2, py: 1, bgcolor: 'action.hover', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                  {(adventure as any).waypointCount !== undefined && (
                    <Box sx={{ fontSize: '11px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 12 }} /> <strong>{(adventure as any).waypointCount}</strong> waypoints
                    </Box>
                  )}
                  {hasStats(adventure) && (
                    <Box sx={{ fontSize: '11px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Visibility sx={{ fontSize: 12 }} /> <strong>{(adventure.uniquePlayersLastYear ?? 0)}</strong> plays
                    </Box>
                  )}
                  {(adventure as any).averageRating > 0 && (
                    <Box sx={{ fontSize: '11px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StarIcon sx={{ fontSize: 12 }} /> <strong>{((adventure as any).averageRating as number).toFixed(1)}</strong> rating
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, p: '10px 16px' }}>
                  <Button
                    component={RouterLink}
                    to={`/adventures/${adventure.id}`}
                    size="small"
                    variant="contained"
                    startIcon={<Visibility sx={{ fontSize: 14 }} />}
                    sx={{ flex: 1, borderRadius: '8px', fontSize: '11px', fontWeight: 600, textTransform: 'none', py: 0.5 }}
                  >
                    View
                  </Button>
                  {((activeTab === 2 && adventure.canManage) || activeTab === 4) && (
                    <Button
                      component={RouterLink}
                      to={`/adventures/${adventure.id}/edit`}
                      size="small"
                      variant="outlined"
                      startIcon={<Edit sx={{ fontSize: 14 }} />}
                      sx={{ flex: 1, borderRadius: '8px', fontSize: '11px', fontWeight: 600, textTransform: 'none', py: 0.5, borderColor: 'info.main', color: 'info.main' }}
                    >
                      Edit
                    </Button>
                  )}
                  {((activeTab === 2 && adventure.canManage) || activeTab === 4) && (
                    <IconButton
                      size="small"
                      onClick={() => handleHistoryClick(adventure)}
                      title="View Change History"
                      sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', width: 32, height: 32 }}
                    >
                      <AccessTime sx={{ fontSize: 13, color: 'text.secondary' }} />
                    </IconButton>
                  )}
                  {((activeTab === 2 && adventure.canManage) || activeTab === 4) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(adventure)}
                      title="Delete Adventure"
                      sx={{ border: 1, borderColor: 'error.light', borderRadius: '8px', width: 32, height: 32 }}
                    >
                      <Delete sx={{ fontSize: 13, color: 'error.main' }} />
                    </IconButton>
                  )}
                  {activeTab === 0 && isJoinedAdventure(adventure) && !adventure.completedDate && (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      startIcon={<ExitToApp sx={{ fontSize: 14 }} />}
                      onClick={() => handleAbandonClick(adventure)}
                      sx={{ flex: 1, borderRadius: '8px', fontSize: '11px', fontWeight: 600, textTransform: 'none', py: 0.5 }}
                    >
                      Abandon
                    </Button>
                  )}
                </Box>
              </Card>
            )
          ))}
        </Box>
      ) : viewMode === 'list' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'title'}
                    direction={sortField === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={hideOnSmall}>Description</TableCell>
                <TableCell sx={hideOnMobile}>
                  <TableSortLabel
                    active={sortField === 'category'}
                    direction={sortField === 'category' ? sortOrder : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                {activeTab === 0 ? (
                  /* Active tab - show Started column */
                  <TableCell sx={hideOnMobile}>Started</TableCell>
                ) : (
                  /* Other tabs - show Groups and Repeatable columns */
                  <>
                    <TableCell align="center" sx={hideOnMobile}>
                      <TableSortLabel
                        active={sortField === 'allowGroups'}
                        direction={sortField === 'allowGroups' ? sortOrder : 'asc'}
                        onClick={() => handleSort('allowGroups')}
                      >
                        Groups
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={hideOnSmall}>
                      <TableSortLabel
                        active={sortField === 'isRepeatable'}
                        direction={sortField === 'isRepeatable' ? sortOrder : 'asc'}
                        onClick={() => handleSort('isRepeatable')}
                      >
                        Repeatable
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={hideOnMobile}>
                      <TableSortLabel
                        active={sortField === 'createdDate'}
                        direction={sortField === 'createdDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('createdDate')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAdventures.map((adventure) => {
                // Determine role icon for Manage tab
                const relation = (adventure as any).userRelationType as number | undefined
                const isOwner = user?.userId === adventure.creatorId
                const isModerator = hasRelation(relation, UserAdventureRelationType.Moderator)
                const isTester = hasRelation(relation, UserAdventureRelationType.Tester)

                // Get role icon and tooltip
                const getRoleIcon = () => {
                  if (isOwner) return <Tooltip title="Owner"><OwnerIcon fontSize="small" sx={{ color: 'warning.main', mr: 0.5 }} /></Tooltip>
                  if (isModerator) return <Tooltip title="Moderator"><ModeratorIcon fontSize="small" sx={{ color: 'info.main', mr: 0.5 }} /></Tooltip>
                  if (isTester) return <Tooltip title="Tester"><TesterIcon fontSize="small" sx={{ color: 'secondary.main', mr: 0.5 }} /></Tooltip>
                  return <Tooltip title="Maintainer"><MaintainerIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} /></Tooltip>
                }

                // Get status icon with color
                const getStatusIcon = () => {
                  const status = adventure.status
                  if (status === AdventureStatus.Draft || status === AdventureStatus.ModeratorReview) {
                    return <Tooltip title="Draft"><DraftIcon fontSize="small" sx={{ color: 'warning.main', mr: 0.5 }} /></Tooltip>
                  }
                  if (status === AdventureStatus.Testing) {
                    return <Tooltip title="Testing"><TestIcon fontSize="small" sx={{ color: 'info.main', mr: 0.5 }} /></Tooltip>
                  }
                  if (status === AdventureStatus.Published) {
                    return <Tooltip title="Published"><PublishedIcon fontSize="small" sx={{ color: 'success.main', mr: 0.5 }} /></Tooltip>
                  }
                  if (status === AdventureStatus.Archived || status === AdventureStatus.Suspended) {
                    return <Tooltip title="Archived"><ClosedIcon fontSize="small" sx={{ color: 'error.main', mr: 0.5 }} /></Tooltip>
                  }
                  return null
                }

                return (
                <TableRow
                  key={adventure.id}
                  hover
                  onClick={() => navigate(`/adventures/${adventure.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                      {activeTab === 2 && getRoleIcon()}
                      {activeTab === 2 && getStatusIcon()}
                      <Typography variant="body2" noWrap>{adventure.title}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...hideOnSmall, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {adventure.description}
                  </TableCell>
                  <TableCell sx={hideOnMobile}>
                    {ADVENTURE_MAIN_CATEGORY_LABELS[(adventure as any).mainCategory as AdventureMainCategory] || 'Adventure'}
                  </TableCell>
                  {activeTab === 0 ? (
                    /* Active tab - show Started date */
                    <TableCell sx={hideOnMobile}>
                      {formatUtcDate(adventureStartedMap.get(adventure.id))}
                    </TableCell>
                  ) : (
                    /* Other tabs - show Groups, Repeatable, Created */
                    <>
                      <TableCell align="center" sx={hideOnMobile}>
                        {adventure.allowGroups ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center" sx={hideOnSmall}>
                        {adventure.isRepeatable ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="disabled" fontSize="small" />}
                      </TableCell>
                      <TableCell sx={hideOnMobile}>
                        {formatUtcDate(adventure.createdDate)}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      component={RouterLink}
                      to={`/adventures/${adventure.id}`}
                      size="small"
                      title="View"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    {((activeTab === 2 && adventure.canManage) || activeTab === 4) && (
                      <IconButton
                        component={RouterLink}
                        to={`/adventures/${adventure.id}/edit`}
                        size="small"
                        title="Edit"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      title="Reviews"
                      onClick={() => handleOpenRatings(adventure)}
                    >
                      <ReviewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Map view - popup first behavior (clicking marker shows popup with View Details)
        mapAdventures.length > 0 ? (
          <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2 }}>
            <AdventureMap
              adventures={mapAdventures}
              height="600px"
            />
          </Paper>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <MapIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Adventures with Location Data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTab === 1
                ? 'Your completed adventures don\'t have location data to display on the map.'
                : 'Adventures in this tab don\'t have location data set.'}
            </Typography>
          </Paper>
        )
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Adventure</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{adventureToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abandon Confirmation Dialog */}
      <Dialog open={abandonDialogOpen} onClose={handleAbandonCancel}>
        <DialogTitle sx={{ color: 'warning.main' }}>Abandon Adventure</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to abandon "{adventureToAbandon?.title}"? Your progress will be lost and you will need to rejoin to continue.
          </DialogContentText>
          {abandonError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {abandonError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAbandonCancel} disabled={isAbandoning}>
            Cancel
          </Button>
          <Button
            onClick={handleAbandonConfirm}
            color="warning"
            variant="contained"
            disabled={isAbandoning}
          >
            {isAbandoning ? 'Abandoning...' : 'Abandon'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Collection Confirmation Dialog */}
      <Dialog open={deleteCollectionDialogOpen} onClose={handleDeleteCollectionCancel}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Collection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the collection "{collectionToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
          {deleteCollectionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteCollectionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCollectionCancel} disabled={deleteCollectionMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCollectionConfirm}
            color="error"
            variant="contained"
            disabled={deleteCollectionMutation.isPending}
          >
            {deleteCollectionMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      {adventureForHistory && (
        <AdventureHistoryDialog
          open={historyDialogOpen}
          onClose={handleHistoryClose}
          adventureId={adventureForHistory.id}
          adventureTitle={adventureForHistory.title}
        />
      )}

      {/* Ratings Dialog */}
      <Dialog
        open={ratingsDialogOpen}
        onClose={handleCloseRatings}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Reviews for "{adventureForRatings?.title}"
        </DialogTitle>
        <DialogContent dividers>
          {ratingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : allRatings.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No reviews yet for this adventure.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {allRatings.map((rating) => (
                <Paper key={rating.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar
                      src={rating.user?.avatarUrl || undefined}
                      sx={{ width: 32, height: 32 }}
                    >
                      {rating.user?.displayName?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {rating.user?.displayName || rating.user?.username || 'Anonymous'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(rating.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Presence:</Typography>
                      <Rating value={rating.presenceScore} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Obscura:</Typography>
                      <Rating value={rating.obscuraScore} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Trailcraft:</Typography>
                      <Rating value={rating.trailcraftScore} readOnly size="small" />
                    </Box>
                    {rating.craftmarkScore && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Craftmark:</Typography>
                        <Rating value={rating.craftmarkScore} readOnly size="small" />
                      </Box>
                    )}
                  </Box>
                  {rating.comment && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{rating.comment}"
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRatings}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stats Popover */}
      <Popover
        open={Boolean(statsAnchorEl)}
        anchorEl={statsAnchorEl}
        onClose={handleStatsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
            Adventure Popularity
          </Typography>
          {statsAdventure && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Last Hour:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {(statsAdventure.uniquePlayersLastHour ?? 0).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Last 24 Hours:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {(statsAdventure.uniquePlayersLast24Hours ?? 0).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Last Month:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {(statsAdventure.uniquePlayersLastMonth ?? 0).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Last Year:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {(statsAdventure.uniquePlayersLastYear ?? 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  )
}
